import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Button } from "@mui/joy";
import { Box, Paper, Tooltip } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  GridColumns,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";
import {
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { Position_Entity } from "../../entities/position";

import { useSelector } from "react-redux";
import { selectedProjectID } from "../../redux/filmsReducer";
import {
  useCreatePositionMutation,
  useDeletePositionMutation,
  useUpdatePositionMutation,
} from "../../redux/position/positionEndpoints";
import { usePositions } from "../../redux/position/positionHooks";
import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";

/**
 * Position
 *
 * @returns {null} Position
 */
export default function Positions() {
  const { positions } = usePositions();
  const [updatePosition] = useUpdatePositionMutation();
  const [deletePosition] = useDeletePositionMutation();

  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();

  const onPositionUpdate = async (_newPosition: Position_Entity) => {
    // Wait for the server rather than resolving on a 50ms timer: the grid must
    // not leave edit mode before the write lands, or the refetch that follows
    // reverts the cell to its pre-edit value.
    await updatePosition({ id: _newPosition.id, body: _newPosition }).unwrap();
    return { ..._newPosition };
  };

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        maxHeight: "calc(100vh - 78px)",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        apiRef={apiRef}
        processRowUpdate={onPositionUpdate}
        rows={positions ?? []}
        columns={positionColumns(dispatch, confirm, deletePosition)}
        showToolbar
        slots={{
          toolbar: PositionToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          cell: { tabIndex: 1 },
        }}
      />
    </Paper>
  );
}

function PositionToolbar() {
  const projectID = useSelector(selectedProjectID);
  const [createPosition] = useCreatePositionMutation();

  const handleNewPosition = () => {
    const newPosition = {
      projectID: projectID,
      name: "",
      share: 0,
      id: -1,
    };
    createPosition(newPosition);
  };

  useHotkeys(`ctrl+enter`, handleNewPosition, {
    enableOnTags: ["INPUT", "SELECT", "TEXTAREA"],
  });

  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          p: 1,
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        <Tooltip title="ctrl + enter">
          <Button variant="soft" size="sm" onClick={handleNewPosition}>
            + New Position
          </Button>
        </Tooltip>
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
      </Box>
    </Toolbar>
  );
}

const positionColumns = (
  dispatch: Dispatch,
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  deletePosition: (id: number) => void
): GridColumns<any> =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      editable: false,
      type: "string",
      width: 40,
    },
    {
      field: "name",
      headerName: "Name",
      editable: true,
      flex: 1,
      type: "string",
    },
    {
      field: "pos",
      headerName: "Order",
      editable: true,
      width: 90,
      type: "number",
    },
    {
      field: "share",
      headerName: "Share",
      editable: true,
      width: 90,
      type: "number",
      valueGetter: getterDivider("share", 100),
      valueSetter: setterDivider("share", 100),
      valueFormatter: (valueOrParams: any) =>
        `${valueOrParams?.value ?? valueOrParams}%`,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (props: { id: any }) => [
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label={`Delete`}
          onClick={() => {
            confirm({
              description: `You are about to delete a position`,
            })
              .then(() => {
                deletePosition(props.id);
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
      ],
    },
  ]);
